// Ad/UGC video tiles used to mount <video autoPlay> unconditionally, so a
// gallery of N video ads opened N simultaneous video streams the moment the
// page rendered — regardless of whether anyone was even looking at a given
// tile. Shrinking the file per tile (backend PR #243 + frontend #60, the
// ~480px `previewVideoUrl` variant) cut bytes per stream but never touched
// the stream *count*, so a 39- or 200-ad page still opened that many
// requests on load. Owner spec, verbatim: "videos shouldn't play or load
// until you have hovered over them, then it should play the whole video."
//
// This component renders a still poster at rest — no <video> element exists
// in the DOM at all, so zero video bytes are requested — and mounts a real
// <video> only while the pointer is over the tile. Un-hovering unmounts it
// again, which both stops playback and (deliberately) drops its buffered
// position, so the tile always replays from frame 0 on the next hover
// instead of resuming mid-clip. Holding position instead would leave the
// tile parked on whatever frame it stopped at — which, once the poster is
// swapped back in on every OTHER un-hovered tile, would look like a broken
// thumbnail rather than a deliberate pause.
//
// Not looped: the ask was to play the WHOLE video on hover, not a short
// repeating preview clip (that's what autoPlay+loop was already doing, and
// isn't the thing being fixed here). If the pointer is still over the tile
// when playback ends, it simply holds on the last frame — no auto-restart.
//
// Muted is required, not optional: `mouseenter` is not a browser "user
// activation" gesture, so an unmuted `.play()` triggered from it is liable
// to be silently blocked by autoplay policy. Muted autoplay is exempt.
//
// Touch has no hover event, so `active` here never flips true from a tap —
// by design. This component has NO tap-to-play or tap-to-modal fallback
// of its own — it relies entirely on whatever wraps it one level up to
// give touch users some way to play the video (typically an onClick that
// opens the full ad detail modal, where the video plays with real
// <video controls>). As of this writing every caller (the AdThumbnail
// call sites + the legacy /ads gallery) does provide that, but it is a
// REQUIREMENT ON THE CALLER, not a guarantee this component makes. This
// already went wrong once: pages/UgcWizard/UgcPreviewRail.tsx briefly
// wired this component into a persistent side-by-side rail with no tap
// fallback at all, silently leaving touch users with no way to play the
// video (see its git history / PR #61). Do not drop this component into
// a new spot unless that spot already has its own tap/click fallback —
// and do not use it for a persistent, always-visible player at all: it
// is built for gallery tiles that are mostly off-screen or unwatched,
// not for a single video meant to keep playing while the user's
// attention (and pointer) moves elsewhere.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Image } from '@chakra-ui/react';

type Props = {
  src:   string;
  poster?: string | null;
  alt?:  string;
  // Merged onto the mounted <video>'s style (and the at-rest <Image>'s,
  // where meaningful) so callers keep their existing sizing/fit.
  style?: React.CSSProperties;
};

export function HoverPlayVideo({ src, poster, alt, style }: Props) {
  const [active, setActive] = useState(false);
  const activate   = useCallback(() => setActive(true), []);
  const deactivate = useCallback(() => setActive(false), []);

  return (
    <Box
      position="relative"
      w="100%" h="100%"
      onMouseEnter={activate}
      onMouseLeave={deactivate}
    >
      {active ? (
        <ActiveVideoTile src={src} poster={poster} style={style} />
      ) : poster ? (
        <Image
          src={poster}
          alt={alt || ''}
          w="100%" h="100%"
          objectFit="contain"
          loading="lazy"
          bg="#000"
        />
      ) : (
        // No poster available for this source (e.g. raw UGC media that
        // never went through the ad pipeline's poster generation) — a
        // plain dark tile with a play glyph beats an unlabeled blank box,
        // and still requests nothing until hovered.
        <Box
          w="100%" h="100%"
          bg="#000"
          display="flex" alignItems="center" justifyContent="center"
        >
          <Box as="span" color="whiteAlpha.700" fontSize="20px" lineHeight={1}>▶</Box>
        </Box>
      )}
    </Box>
  );
}

// Mounted only while the tile is hovered (see the ternary above) — its
// own mount/unmount IS the hover-in/hover-out transition, so the effect
// setup/teardown below doubles as the play-on-hover / stop-on-unhover
// lifecycle. Split out from HoverPlayVideo so the ref + effect only
// exist while a real <video> element does.
function ActiveVideoTile({ src, poster, style }: {
  src: string;
  poster?: string | null;
  style?: React.CSSProperties;
}) {
  const ref = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // `src` is assigned HERE, imperatively — deliberately NOT as a JSX prop
    // on the <video> below. Verified against a real bug, not a style
    // preference: with `src` only as a JSX prop, React only ever (re)writes
    // the DOM attribute when the prop VALUE changes between renders. React
    // 18 StrictMode double-invokes effects in dev (mount → run effect →
    // simulate-unmount via the cleanup below, which strips `src` → run the
    // effect again) to catch exactly this class of bug — and it did: the
    // second `el.play()` had a `src`-less element to work with, since the
    // JSX prop's value hadn't changed and React saw nothing to reapply, so
    // it never actually loaded (confirmed via headless Chrome: paused=false
    // but networkState stayed NETWORK_EMPTY forever). Any real fast
    // unmount/remount cycle (not just StrictMode) would hit the same wall.
    // Setting `el.src` directly in the same effect that calls play() makes
    // every setup self-sufficient regardless of what the teardown before it
    // cleared.
    el.src = src;
    // Driven imperatively (not the `autoPlay` attribute) specifically so
    // the returned Promise has a .catch(). A fast pointer sweep across
    // many tiles mounts and then immediately unmounts this element before
    // playback has actually started, which rejects the play() promise
    // with an AbortError. That's expected here, not a bug — but with no
    // handler it surfaces as an unhandled promise rejection in the
    // console for every tile swept over, one per sweep.
    el.play().catch(() => {});
    return () => {
      // Un-hovering must actually stop the in-flight request, not just
      // remove the element from the DOM — Chrome keeps a <video>'s
      // in-flight / buffered download alive for a while after the
      // element unmounts unless it's explicitly told to drop it.
      // pause() alone does not do that; clearing src and calling load()
      // does, which is the actual bandwidth win this component exists
      // for (a still poster with zero <video> elements at rest is only
      // half the story if a swept-past tile keeps downloading anyway).
      el.pause();
      el.removeAttribute('src');
      el.load();
    };
  }, [src]);

  return (
    <video
      ref={ref}
      poster={poster || undefined}
      muted
      playsInline
      // "metadata", not "auto": this element can unmount a few ms after
      // mounting on a fast pointer sweep (see the effect above), and
      // "auto" has the browser start eagerly buffering the full file the
      // instant the element exists in the DOM — before play() even gets
      // a chance to run — which is exactly the per-tile bandwidth this
      // component exists to avoid paying for hovers that never commit.
      // "metadata" only fetches enough for duration/dimensions/first
      // frame up front; the real fetch only starts once play() actually
      // requests data (immediately below, for a hover that sticks). The
      // component this replaced used "metadata" for the same reason.
      preload="metadata"
      style={{
        width: '100%', height: '100%',
        objectFit: 'contain', display: 'block',
        background: '#000',
        ...style
      }}
    />
  );
}
